import { useCallback, useEffect, useMemo, useState } from "react";
import { api, Video, VideoPage } from "../lib/api";
import VideoUploader from "./VideoUploader";
import VideoPlayer from "./VideoPlayer";

/**
 * Videos tab of the case workspace.
 *
 * Three regions:
 *   1. Uploader  — chunked uploader card at the top.
 *   2. Library   — paginated list of videos with status pills, progress,
 *                  open / reprocess / delete actions.
 *   3. Player    — full scene strip + transcript pane shown inline when
 *                  the user opens a specific video.
 *
 * The list polls itself while any row is in flight (uploading, queued,
 * processing) so the SPA tracks scene-detect + transcribe progress
 * without manual refresh — same UX as the documents tab.
 */

const PAGE_SIZE = 12;

interface Props {
  caseId: number;
  active: boolean;
  /** Fired whenever a video is added, removed, or reprocessed so the
   *  parent can refresh the case (video_count badge in the edge rail). */
  onChanged?: () => void;
}

export default function VideosView({ caseId, active, onChanged }: Props) {
  const [page, setPage] = useState<VideoPage | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [opened, setOpened] = useState<Video | null>(null);

  const load = useCallback(
    async (silent = false): Promise<VideoPage | null> => {
      if (!silent) setBusy(true);
      setErr(null);
      try {
        const offset = (pageNum - 1) * PAGE_SIZE;
        const dp = await api.get<VideoPage>(
          `/cases/${caseId}/videos?limit=${PAGE_SIZE}&offset=${offset}`,
        );
        setPage(dp);
        return dp;
      } catch (e: any) {
        setErr(e?.message ?? "Failed to load videos");
        return null;
      } finally {
        if (!silent) setBusy(false);
      }
    },
    [caseId, pageNum],
  );

  // Initial + pagination-driven load.
  useEffect(() => {
    void load();
  }, [load]);

  // Poll while anything is still moving. We piggy-back on `page` so the
  // poller stops the moment everything is ready/failed. This also keeps
  // running while a row is past `status='ready'` but still has its
  // background timeline-extraction job in flight, so the player picks
  // up newly-generated events without the user needing to refresh.
  const pendingCount = useMemo(() => {
    if (!page) return 0;
    const baseline =
      page.status_counts.uploading +
      page.status_counts.queued +
      page.status_counts.processing;
    const timelinePending = page.items.reduce((n, v) => {
      return v.timeline_status === "queued" || v.timeline_status === "running"
        ? n + 1
        : n;
    }, 0);
    return baseline + timelinePending;
  }, [page]);

  useEffect(() => {
    if (!active) return;
    if (pendingCount <= 0) return;
    const t = setInterval(() => {
      void load(true);
    }, 3000);
    return () => clearInterval(t);
  }, [active, pendingCount, load]);

  // If the user has a viewer open and the underlying row updates (e.g.
  // status flips to ready, scene_count goes up), keep the opened view
  // in sync by re-reading from the latest page.
  useEffect(() => {
    if (!opened || !page) return;
    const fresh = page.items.find((v) => v.id === opened.id);
    if (fresh && fresh.updated_at !== opened.updated_at) {
      setOpened(fresh);
    }
  }, [page, opened]);

  const onUploaded = useCallback(
    (v: Video) => {
      // Drop back to page 1 so the user sees their fresh upload top of list.
      if (pageNum !== 1) setPageNum(1);
      void load(true);
      onChanged?.();
      // Don't auto-open — they probably want to see the queued/processing
      // state in the list rather than the empty player frame. They can
      // click to open once it's ready.
      void v;
    },
    [load, pageNum, onChanged],
  );

  const onDelete = useCallback(
    async (video: Video) => {
      if (!confirm(`Delete "${video.original_name}"? This can't be undone.`)) return;
      try {
        await api.del(`/cases/${caseId}/videos/${video.id}`);
        if (opened?.id === video.id) setOpened(null);
        await load(true);
        onChanged?.();
      } catch (e: any) {
        setErr(e?.message ?? "Delete failed");
      }
    },
    [caseId, opened, load, onChanged],
  );

  const onReprocess = useCallback(
    async (video: Video) => {
      try {
        await api.post<Video>(`/cases/${caseId}/videos/${video.id}/reprocess`);
        await load(true);
      } catch (e: any) {
        setErr(e?.message ?? "Reprocess failed");
      }
    },
    [caseId, load],
  );

  const total = page?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="videos-tab">
      <VideoUploader caseId={caseId} onUploaded={onUploaded} />

      {err && <div className="alert alert--error">{err}</div>}

      {opened && (
        <div className="videos-tab__player-wrap">
          <div className="videos-tab__player-head">
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => setOpened(null)}
            >
              ← Back to list
            </button>
          </div>
          <VideoPlayer caseId={caseId} video={opened} />
        </div>
      )}

      {!opened && (
        <div className="videos-tab__list">
          <div className="videos-tab__list-head">
            <h3>Videos</h3>
            <span className="muted">
              {total === 0
                ? "No videos uploaded yet."
                : `${total} video${total === 1 ? "" : "s"} · ${
                    page?.status_counts.ready ?? 0
                  } ready`}
            </span>
          </div>

          {busy && !page ? (
            <div className="muted">Loading…</div>
          ) : page && page.items.length > 0 ? (
            <ul className="videos-tab__items">
              {page.items.map((v) => (
                <VideoRow
                  key={v.id}
                  video={v}
                  onOpen={() => setOpened(v)}
                  onDelete={() => void onDelete(v)}
                  onReprocess={() => void onReprocess(v)}
                />
              ))}
            </ul>
          ) : (
            <div className="muted videos-tab__empty">
              Upload a video to extract scenes and a clickable transcript, or
              an audio file (MP3/WAV) for a searchable transcript.
            </div>
          )}

          {totalPages > 1 && (
            <div className="videos-tab__pager">
              <button
                type="button"
                className="btn btn--sm"
                disabled={pageNum <= 1}
                onClick={() => setPageNum((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="muted">
                Page {pageNum} of {totalPages}
              </span>
              <button
                type="button"
                className="btn btn--sm"
                disabled={pageNum >= totalPages}
                onClick={() => setPageNum((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Row                                                                        //
// --------------------------------------------------------------------------- //

function VideoRow({
  video,
  onOpen,
  onDelete,
  onReprocess,
}: {
  video: Video;
  onOpen: () => void;
  onDelete: () => void;
  onReprocess: () => void;
}) {
  const canOpen = video.status === "ready" || video.status === "processing";
  const showProgress = video.status === "uploading" || video.status === "queued" || video.status === "processing";

  return (
    <li className={`videos-tab__row videos-tab__row--${video.status}`}>
      <div className="videos-tab__row-main">
        <div className="videos-tab__row-title">
          <button
            type="button"
            className="videos-tab__row-name"
            onClick={onOpen}
            disabled={!canOpen}
          >
            {video.original_name}
          </button>
          <span className={`badge badge--${video.status}`}>{statusLabel(video.status)}</span>
        </div>
        <div className="videos-tab__row-meta">
          {formatDuration(video.duration_seconds)}
          {video.width && video.height ? ` · ${video.width}×${video.height}` : ""}
          {" · "}
          {formatBytes(video.size_bytes)}
          {video.scene_count > 0 && ` · ${video.scene_count} scenes`}
          {video.transcript_segment_count > 0 &&
            ` · ${video.transcript_segment_count} transcript segments`}
        </div>
        {showProgress && (
          <div className="videos-tab__row-progress">
            <div
              className="videos-tab__row-bar"
              role="progressbar"
              aria-valuenow={video.progress_pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="videos-tab__row-bar-fill"
                style={{ width: `${Math.max(2, video.progress_pct)}%` }}
              />
            </div>
            <span className="muted">
              {progressLabel(video)}
            </span>
          </div>
        )}
        {video.status === "failed" && video.error_message && (
          <div className="alert alert--error videos-tab__row-error">
            {video.error_message}
          </div>
        )}
      </div>
      <div className="videos-tab__row-actions">
        {canOpen && (
          <button type="button" className="btn btn--sm" onClick={onOpen}>
            Open
          </button>
        )}
        {(video.status === "ready" || video.status === "failed") && (
          <button
            type="button"
            className="btn btn--sm"
            onClick={onReprocess}
            title="Re-run scene detection, captions, and transcription"
          >
            Reprocess
          </button>
        )}
        <button
          type="button"
          className="btn btn--sm btn--danger"
          onClick={onDelete}
        >
          Delete
        </button>
      </div>
    </li>
  );
}

function statusLabel(status: Video["status"]): string {
  switch (status) {
    case "uploading":
      return "Uploading";
    case "queued":
      return "Queued";
    case "processing":
      return "Processing";
    case "ready":
      return "Ready";
    case "failed":
      return "Failed";
  }
}

function progressLabel(video: Video): string {
  if (video.status === "uploading") return "Uploading bytes…";
  if (video.status === "queued") return "Waiting for the worker…";
  const step = video.progress_step;
  if (!step) return `Processing (${video.progress_pct}%)`;
  const label = STEP_LABELS[step] ?? step;
  return `${label} (${video.progress_pct}%)`;
}

const STEP_LABELS: Record<string, string> = {
  probe: "Inspecting video",
  scenes: "Detecting scenes",
  frames: "Extracting keyframes",
  vision: "Captioning scenes",
  transcribe: "Transcribing audio",
};

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDuration(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return "—";
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
}
